import { chmodSync, mkdirSync, rmSync, writeFileSync } from "node:fs";
import { dirname } from "node:path";

import { CliError } from "../cli/cli-error";
import { DEFAULT_BASE_URL } from "@/lib/shared/config-constants";
import type { CliConfig } from "@/lib/cli/cli-config";
import { CONFIG_PATH } from "@/lib/path/config-path-constants";
import { readFileAsObject } from "@/lib/file/read-file-as-object";
import { configReadString } from "@/lib/string/config-read-string";
import { readUser } from "@/lib/user/read-user";
import { readHttpUrl } from "@/lib/http/read-http-url";
import { isSameOrigin } from "@/lib/same/is-same-origin";

/**
 * `~/.hepha/config.json`, where a device-flow session lives between runs.
 *
 * Written 0600, because it holds a token that acts as the human. Agents are
 * expected to use HEPHA_API_KEY instead and never touch this file.
 */
export const config = {
  path: CONFIG_PATH,

  load(): CliConfig {
    const stored = readFileAsObject();
    const storedBaseUrl = readHttpUrl(stored.baseUrl);

    // A malformed HEPHA_URL is refused rather than ignored. Falling back would
    // run the command against production, with the production token, since
    // the origin would then match, which is the exact accident the
    // origin-binding below exists to prevent. A malformed value in the config
    // FILE still falls back: the user did not ask for it this run, and
    // bricking every command over a stale file helps nobody.
    const override = configReadString(process.env.HEPHA_URL);
    if (override && !readHttpUrl(override)) {
      throw new CliError(
        `HEPHA_URL is not a valid http(s) URL: "${override}".`,
        2,
      );
    }

    const baseUrl = readHttpUrl(override) ?? storedBaseUrl ?? DEFAULT_BASE_URL;

    // The stored token is bound to the deployment it was issued by. Pointing
    // HEPHA_URL at staging or somebody else's instance must not send a human
    // bearer token for production to that origin; it would not authenticate
    // there, and it would hand the credential to whoever runs it.
    const token =
      storedBaseUrl && isSameOrigin(storedBaseUrl, baseUrl)
        ? configReadString(stored.token)
        : undefined;
    const user = token ? readUser(stored.user) : undefined;

    return {
      baseUrl,
      ...(token ? { token } : {}),
      ...(user ? { user } : {}),
    };
  },

  save(next: CliConfig): void {
    mkdirSync(dirname(CONFIG_PATH), { recursive: true, mode: 0o700 });
    writeFileSync(CONFIG_PATH, `${JSON.stringify(next, null, 2)}\n`, {
      mode: 0o600,
    });
    chmodSync(CONFIG_PATH, 0o600);
  },

  clear(): void {
    rmSync(CONFIG_PATH, { force: true });
  },
};
